import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { X, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CTA_ROUTES = {
    open_coach: '/coach',
    open_health: '/health',
    open_fitness: '/fitness',
    open_sleep: '/sleep',
    open_nutrition: '/nutrition',
};

/**
 * Daily Briefing Card — shows ONE personalised insight per user per day.
 * Source of variety: 7 backend insight types rotate to avoid repetition.
 * Dismiss = hides for the day (still re-fetchable tomorrow).
 *
 * The `routine_due` type ships an inline quick-log CTA so the user can
 * fix the overdue routine in a single tap, without leaving the dashboard.
 */
export function DailyBriefingCard() {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [briefing, setBriefing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);
    const [quickLogging, setQuickLogging] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/briefing/today?language=${language}`);
                if (cancelled) return;
                const data = res.data;
                if (data && !data.empty && !data.dismissed_at) {
                    setBriefing(data);
                }
            } catch (_e) { /* silent */ }
            finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [language]);

    const handleDismiss = async () => {
        setDismissed(true);
        try {
            await axios.post(`${API}/briefing/dismiss`, {});
        } catch (_e) { /* non-fatal */ }
    };

    const handleCta = () => {
        const route = CTA_ROUTES[briefing?.cta_action];
        if (route) navigate(route);
    };

    const handleRoutineQuickLog = async () => {
        const top = briefing?.routine_items?.[0];
        if (!top || quickLogging) return;
        setQuickLogging(true);
        try {
            const grams = top.serving_quantity_g || 100;
            const factor = grams / 100;
            const calories = Math.round((top.kcal_100g || 0) * factor);
            const protein = Math.round((top.protein_100g || 0) * factor * 10) / 10;
            const carbs = Math.round((top.carbs_100g || 0) * factor * 10) / 10;
            const fat = Math.round((top.fat_100g || 0) * factor * 10) / 10;
            const label = top.brand ? `${top.brand} ${top.name}` : top.name;
            // Use the time-of-day as a sensible meal_type default — backend
            // is forgiving here.
            const hour = new Date().getHours();
            const mealType =
                hour < 11 ? 'breakfast' :
                hour < 15 ? 'lunch' :
                hour < 18 ? 'snack' : 'dinner';
            await axios.post(`${API}/nutrition/food`, {
                name: `${label} (${grams}g)`,
                calories, protein, carbs, fat,
                meal_type: mealType,
            });
            toast.success(
                language === 'de'
                    ? `Geloggt: ${label} (+${calories} kcal) 🥄`
                    : `Logged: ${label} (+${calories} kcal) 🥄`,
                { duration: 4000 }
            );
            // Hide the briefing for today — the routine is satisfied.
            handleDismiss();
        } catch (_e) {
            toast.error(
                language === 'de' ? 'Loggen fehlgeschlagen' : 'Log failed',
                { duration: 4000 }
            );
        } finally {
            setQuickLogging(false);
        }
    };

    if (loading || dismissed || !briefing) return null;

    const isRoutine = briefing.type === 'routine_due' && briefing.routine_items?.length > 0;
    const top = briefing.routine_items?.[0];

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35 }}
                data-testid="daily-briefing-card"
            >
                <Card className={`overflow-hidden ${isRoutine
                    ? 'border-amber-400/50 bg-gradient-to-br from-amber-500/5 via-card to-card'
                    : 'border-primary/30 bg-gradient-to-br from-primary/5 via-card to-card'}`}>
                    <CardContent className="p-4 relative">
                        <button
                            type="button"
                            onClick={handleDismiss}
                            aria-label={language === 'de' ? 'Briefing ausblenden' : 'Dismiss briefing'}
                            data-testid="briefing-dismiss"
                            className="absolute top-2 right-2 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="flex items-start gap-3 pr-6">
                            <div className="text-3xl shrink-0 leading-none mt-0.5" aria-hidden="true">
                                {briefing.emoji || '🐺'}
                            </div>
                            <div className="min-w-0 flex-1 space-y-1">
                                <p className={`text-[10px] uppercase tracking-wide font-semibold ${isRoutine ? 'text-amber-600 dark:text-amber-400' : 'text-primary'}`}>
                                    {isRoutine
                                        ? (language === 'de' ? 'Routine-Erinnerung' : 'Routine reminder')
                                        : (language === 'de' ? 'Nanouks Tagesimpuls' : "Nanouk's daily nudge")}
                                </p>
                                <h3 className="font-display font-bold text-sm leading-tight" data-testid="briefing-headline">
                                    {briefing.headline}
                                </h3>
                                <p className="text-xs text-muted-foreground leading-snug" data-testid="briefing-body">
                                    {briefing.body}
                                </p>
                                {isRoutine && top && top.kcal_100g != null && (
                                    <button
                                        type="button"
                                        onClick={handleRoutineQuickLog}
                                        disabled={quickLogging}
                                        data-testid="briefing-routine-quicklog"
                                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 transition-colors"
                                    >
                                        {quickLogging
                                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            : <span aria-hidden="true">🥄</span>}
                                        {language === 'de'
                                            ? `Jetzt ${top.name} loggen`
                                            : `Log ${top.name} now`}
                                    </button>
                                )}
                                {isRoutine && top && top.kcal_100g == null && (
                                    <p className="mt-2 text-[10px] text-muted-foreground italic" data-testid="briefing-routine-no-kcal">
                                        {language === 'de'
                                            ? '(Nährwerte fehlen — bitte manuell loggen)'
                                            : '(Nutrition data missing — log manually)'}
                                    </p>
                                )}
                                {!isRoutine && briefing.cta_label && briefing.cta_action && (
                                    <button
                                        type="button"
                                        onClick={handleCta}
                                        data-testid="briefing-cta"
                                        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                                    >
                                        {briefing.cta_label}
                                        <ArrowRight className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </AnimatePresence>
    );
}
